import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Fragment } from '@atlaskit/editor-prosemirror/model';

/**
 * Compare valid requested replacement structure with the applied target. Parse through
 * the editor schema to fill default attrs; localIds and editor-only attrs are not intent.
 * Text is deliberately excluded: this signal measures structural loss, not copy changes.
 */
function structure(node: PMNode): unknown[] {
	const stableValue = (value: unknown): unknown => {
		if (Array.isArray(value)) {
			return value.map(stableValue);
		}
		if (value !== null && typeof value === 'object') {
			return Object.fromEntries(
				Object.entries(value)
					.sort(([a], [b]) => a.localeCompare(b))
					.map(([key, item]) => [key, stableValue(item)]),
			);
		}
		return value;
	};
	const attrs = (values: Record<string, unknown>) =>
		stableValue(
			Object.fromEntries(
				Object.entries(values).filter(([key]) => key !== 'localId' && !key.startsWith('__')),
			),
		);
	const children: unknown[][] = [];
	node.forEach((child) => {
		// Adjacent text runs can legitimately merge during application.
		if (!child.isText) {
			children.push(structure(child));
		}
	});
	return [node.type.name, attrs(node.attrs), children];
}

/**
 * `undefined` means the request cannot safely be verified (partial/invalid ADF,
 * unresolved target, or replacement requiring schema fitting). Never blame the apply
 * layer for those cases. The raw final chunk is used before streamer transforms can
 * flatten requested wrappers. Only JSON ADF replaceNode responses are verified here.
 */
export function hasReplacementStructureMismatch(
	before: PMNode,
	after: PMNode,
	targetLocalId: unknown,
	response: string,
): boolean | undefined {
	if (typeof targetLocalId !== 'string' || targetLocalId.length === 0) {
		return undefined;
	}
	try {
		const requested = JSON.parse(response);
		if (
			requested?.type !== 'doc' ||
			!Array.isArray(requested.content) ||
			!requested.content.length
		) {
			return undefined;
		}
		const replacement = Fragment.fromArray(
			requested.content.map((json: unknown) => before.type.schema.nodeFromJSON(json)),
		);
		replacement.forEach((node) => node.check());
		let targetPos: number | undefined;
		// Stop at the second match: duplicate IDs make the applied region ambiguous.
		const findTarget = (parent: PMNode, start: number): boolean => {
			let pos = start;
			for (let index = 0; index < parent.childCount; index++) {
				const node = parent.child(index);
				if (node.attrs.localId === targetLocalId) {
					if (targetPos !== undefined) {
						return false;
					}
					targetPos = pos;
				}
				if (!findTarget(node, pos + 1)) {
					return false;
				}
				pos += node.nodeSize;
			}
			return true;
		};
		if (!findTarget(before, 0) || targetPos === undefined) {
			return undefined;
		}
		const resolved = before.resolve(targetPos);
		// List replacements can legitimately unwrap or lift out of the target's
		// ancestors during slice fitting. The queue may already receive a synthetic
		// task/decision-list wrapper, so these are not safe structural comparisons.
		const listTypes = new Set(['bulletList', 'orderedList', 'taskList', 'decisionList']);
		let replacesWithList = false;
		replacement.forEach((node) => {
			replacesWithList ||= listTypes.has(node.type.name);
		});
		if (replacesWithList) {
			for (let depth = resolved.depth; depth > 0; depth--) {
				if (listTypes.has(resolved.node(depth).type.name)) {
					return undefined;
				}
			}
		}
		const index = resolved.index();
		if (!resolved.parent.canReplace(index, index + 1, replacement)) {
			return undefined;
		}
		// Follow the original target's ancestry, not its localId: replacements may
		// regenerate every ID. Comparing only this region avoids unrelated panels
		// elsewhere in the document masking a dropped wrapper.
		let parent = after;
		for (let depth = 0; depth < resolved.depth; depth++) {
			const child = parent.maybeChild(resolved.index(depth));
			if (!child || child.type !== resolved.node(depth + 1).type) {
				return true;
			}
			parent = child;
		}
		let mismatch = false;
		replacement.forEach((node, _offset, childIndex) => {
			const applied = parent.maybeChild(index + childIndex);
			if (!applied || JSON.stringify(structure(node)) !== JSON.stringify(structure(applied))) {
				mismatch = true;
			}
		});
		return mismatch;
	} catch {
		return undefined;
	}
}
